'use client'

import { createContext, ReactNode, RefObject, useContext } from 'react'

export interface SearchPickerOption {
    id: string | number
    content: ReactNode
    query: string
    /**
     * Optional, already-translated group heading. Consecutive options sharing the same
     * group are rendered under one heading by SearchPicker.OptionsList. Leave undefined
     * for an ungrouped (flat) list.
     */
    group?: string
    // Allow additional data that might be needed for saving the option
    data?: unknown
}

export interface SearchPickerSettings {
    minHeight: number
    maxHeight: number
    marginBottom: number
    debounceMs: number
    inputFieldHeight: number
    /**
     * Empty the input when the picker closes without a pick while no option is selected, so the
     * field never shows text that was not committed. Off by default: a picker without a selected
     * option otherwise keeps what was typed. With a selected option the input always returns to
     * that option's query.
     */
    clearUnselectedQuery: boolean
}

export const DEFAULT_SETTINGS: SearchPickerSettings = {
    minHeight: 200,
    maxHeight: 500,
    marginBottom: 20,
    debounceMs: 150,
    inputFieldHeight: 46,
    clearUnselectedQuery: false,
}

export interface ClosePickerOptions {
    selectOption?: SearchPickerOption
    autoSelect?: boolean
}

export interface SearchPickerContextValue {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    closePicker: (options?: ClosePickerOptions) => void
    contentRef: RefObject<HTMLDivElement>
    maxHeight: number
    setMaxHeight: (maxHeight: number) => void
    query: string
    setQuery: (query: string) => void
    search: (query: string) => void
    options: SearchPickerOption[]
    setOptions: (options: SearchPickerOption[]) => void
    isLoading: boolean
    hasNoResults: boolean
    selectedOption?: SearchPickerOption
    onSelectOption?: (option: SearchPickerOption) => void
    onResetOption?: () => void
    settings: SearchPickerSettings
}

const SearchPickerContext = createContext<SearchPickerContextValue | undefined>(undefined)

export const useSearchPickerContext = () => {
    const context = useContext(SearchPickerContext)
    if (context === undefined) {
        throw new Error('useSearchPickerContext must be used within a SearchPicker.Root')
    }
    return context
}

export interface SearchPickerProviderProps {
    children: ReactNode
    value: SearchPickerContextValue
}

export const SearchPickerProvider = ({ children, value }: SearchPickerProviderProps) => {
    return <SearchPickerContext.Provider value={value}>{children}</SearchPickerContext.Provider>
}
